/** @jsx isolateComponent */
import { useState } from 'react';

import {
  Icon,
  Link,
  Spacer,
  Spinner,
  TextInput,
} from '@zapier/design-system';
import { emit } from '@zapier/events-toolkit-browser';
import { DocLinks, Surface } from '@zapier/product-get-help';
import { isolateComponent } from '@zapier/style-encapsulation';

import { USER_INTERACTION_EVENT } from 'app/get-help/constants';

import useFederatedSearchResults from 'app/get-help/hooks/useFederatedSearchResults';
import { SizeType, TrackingFields } from '@zapier/product-get-help/src/types';

type HelpCenterSuggestionsProps = {
  /** Custom title to use. */
  title?: string;
  /** Size to display. */
  size?: SizeType;
};

export const HelpCenterSuggestions = ({
  size = 'large',
}: HelpCenterSuggestionsProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { results, loading, error } = useFederatedSearchResults(searchQuery);
  const selectionTrackingFields: TrackingFields = {
    interactionName: 'federated_search_selection',
    interactionGoal: 'Clicked on Federated Search selection',
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent default form submission
    const form = e.currentTarget;
    const query = form.querySelector('input[name="query"]') as HTMLInputElement;
    if (query && query.value.trim()) {
      setSearchQuery(query.value);
      emit(
        {
          system_name: 'support',
          interaction_name: 'federated_search_entered',
          interaction_goal: 'Entered a search term in Federated Search',
          event_action: 'search',
          option_selected: query.value,
          custom_tracking_fields: {
            federated_search_string: query.value,
          },
        },
        USER_INTERACTION_EVENT
      );
    }
  };

  /**
   * Format the results to match the expected shape for the DocLinks component.
   * @param results - The results to format.
   * @returns The formatted results.
   * */
  const formattedResults = results.map(result => ({
    title: result.title,
    href: result.url,
  }));

  return (
    <Surface
      backgroundColor="neutral100"
      size={size}
      title="Search for Answers"
    >
      <p>
        Find answers and insights from our{' '}
        <Link
          href="https://help.zapier.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Help Center
        </Link>
        ,{' '}
        <Link
          href="https://blog.zapier.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Blog
        </Link>
        ,{' '}
        <Link
          href="https://community.zapier.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Community
        </Link>
        , and{' '}
        <Link
          href="https://learn.zapier.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Zapier Learn
        </Link>{' '}
        platform.
      </p>
      <Spacer height={30} />
      <form onSubmit={handleSubmit}>
        <TextInput
          name="query"
          placeholder="Search our knowledge base"
          renderIconBefore={({ iconSize }: { iconSize: number }) => {
            if (loading) {
              return <Spinner size="small" />;
            } else {
              return <Icon name="magnifyingGlass" size={iconSize} />;
            }
          }}
          size={'small'}
        />
      </form>
      <Spacer height={30} />
      {loading ? (
        <p>Loading...</p>
      ) : (
        <DocLinks
          links={formattedResults}
          size={size}
          trackingFields={selectionTrackingFields}
        />
      )}
      {error && <p>Error: {error}</p>}
    </Surface>
  );
};
